import Grid from '@mui/material/Grid';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext.tsx';
import { FiltersDropdown } from '../components/FiltersDropdown.tsx';
import { SectionTitle } from '../components/SectionTitle.tsx';
import { SelectedFilters } from '../components/SelectedFilters.tsx';
import {
  Filters,
  getFilterOptions,
  SupplementalAdQualityField,
  type SupplementalAdQualityFormState,
} from '../constants.ts';

export function IndustrySection() {
  const { useMarketBrandGroupsFetch } = useDealContext();

  const { setValue, control } = useFormContext<SupplementalAdQualityFormState>();

  const observableBrandLabels = useWatch({ control, name: 'brand_labels' });
  const industriesOp = observableBrandLabels?.op ?? 'current';
  const industriesIds = observableBrandLabels?.ids ?? [];

  const { t } = useTranslation();
  const filterOptions = useMemo(() => getFilterOptions(t), [t]);
  const { marketBrandGroupsLoading, refetchMarketBrandGroups, marketBrandGroups } = useMarketBrandGroupsFetch();

  useEffect(() => {
    if (industriesOp === Filters.ALLOW) {
      refetchMarketBrandGroups();
    }
  }, [refetchMarketBrandGroups, industriesOp]);

  const handleFilterTypeChange = useCallback(
    (_event, value) => {
      setValue(SupplementalAdQualityField.INDUSTRIES_IDS, []);
      setValue(SupplementalAdQualityField.INDUSTRIES_OP, value);
    },
    [setValue]
  );

  const handleChange = useCallback(
    (value: string[]) => {
      setValue(SupplementalAdQualityField.INDUSTRIES_IDS, value);
    },
    [setValue]
  );

  return (
    <>
      <SectionTitle title={t('Industries')} gutterTop={false} />
      <Grid container spacing={1}>
        <Grid size={6}>
          <RHFAutoComplete
            name={SupplementalAdQualityField.INDUSTRIES_OP}
            textFieldProps={{ margin: 'dense' }}
            disableClearable={true}
            getOptionLabel={option => filterOptions[option]?.name || option}
            options={Object.keys(filterOptions)}
            onChange={handleFilterTypeChange}
            fullWidth
            data-test="saq-industries"
          />
        </Grid>
        {industriesOp === Filters.ALLOW && (
          <Grid size={6}>
            <FiltersDropdown
              options={marketBrandGroups}
              loading={marketBrandGroupsLoading}
              selectedOptions={industriesIds}
              placeholder={t('[ Search An Industries ]')}
              onChange={handleChange}
              dataTest="industries-search"
            />
          </Grid>
        )}
      </Grid>
      {industriesIds.length > 0 && (
        <SelectedFilters
          selectedOptions={industriesIds}
          handleChange={handleChange}
          options={marketBrandGroups}
          title={`${t('Industries')} [ ${industriesIds.length} ]`}
          filtersTitle={filterOptions.allow.name}
        />
      )}
    </>
  );
}
